import React, { useEffect, useState } from 'react';
import { subscribeToAwaitingSupportChats, SUPPORT_UNREAD_CAP } from './lib/supportChats.js';
import './adminTokens.css';
import './AdminShell.css';
import OverviewPage from './pages/OverviewPage.jsx';
import VerificationPage from './pages/VerificationPage.jsx';
import SupportInboxPage from './pages/SupportInboxPage.jsx';
import ReportsPage from './pages/ReportsPage.jsx';
import BusinessesPage from './pages/BusinessesPage.jsx';
import UsersPage from './pages/UsersPage.jsx';
import ActivityPage from './pages/ActivityPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';

// ============================================================
// Aangan Super Admin — application shell.
//
// Renders sidebar navigation, top bar, and the currently-selected
// page component. Only mounted by AdminApp AFTER authorization has
// been granted (superAdmin === 1) — never sees signed-out or denied
// states. All auth handling stays in AdminApp; this shell is purely
// for post-auth navigation and page rendering.
//
// ROUTING
// -------
// URL-based via ?admin=1&page=<slug>. Nav clicks call history.pushState
// so the browser back/forward buttons work and page URLs are shareable.
// A popstate listener syncs internal state to the URL when the user
// uses back/forward.
//
// Unknown or missing `page` param falls back to Overview silently. No
// 404 — behavior matches how V2's ?v2= gating handles unknown values.
//
// COST DISCIPLINE
// ---------------
// Sidebar badges were intentionally NOT rendered at first: wiring them
// naively would mean reads on every admin page load even when the admin
// never visits those pages. Each surface decides how to expose its count
// so the cost is explicit and opt-in.
//
// Support Inbox (Session 5d.2) is the first badge to land. It subscribes
// to chats where chatType=='support' AND awaitingAdmin==true — the filter
// IS the cost control, since the query returns only the chats actually
// waiting on a reply (usually zero to a few), not every support chat.
// The awaitingAdmin boolean is maintained server-side by
// onChatMessageCreated. See subscribeToSupportUnread in lib/supportChats.js
// for why a lastReadAt comparison was not viable.
//
// Verification and Reports remain unbadged; each needs its own cost
// decision when someone wants them.
// ============================================================

// Page registry: URL slug → { component, sidebar metadata, top bar copy }.
// Section groupings match the prototype sidebar's Workspace / Manage / System.
const PAGES = {
  overview: {
    Component: OverviewPage,
    title: 'Overview',
    subtitle: "A quick view of what's happening on Aangan today",
    icon: '🏠',
    label: 'Overview',
    section: 'Workspace',
  },
  verification: {
    Component: VerificationPage,
    title: 'Verification Queue',
    subtitle: 'Review businesses awaiting verification',
    icon: '🔍',
    label: 'Verification Queue',
    section: 'Workspace',
  },
  supportInbox: {
    Component: SupportInboxPage,
    title: 'Support Inbox',
    subtitle: 'Support chats from business owners and members',
    icon: '💬',
    label: 'Support Inbox',
    section: 'Workspace',
  },
  reports: {
    Component: ReportsPage,
    title: 'Reports',
    subtitle: 'Member reports across all batches · resolved by batch admins, watched here',
    icon: '🚩',
    label: 'Reports',
    section: 'Workspace',
  },
  businesses: {
    Component: BusinessesPage,
    title: 'Businesses',
    subtitle: 'Manage business listings and access status',
    icon: '🏪',
    label: 'Businesses',
    section: 'Manage',
  },
  users: {
    Component: UsersPage,
    title: 'Users & Growth Partners',
    subtitle: 'Manage users and assign Aangan Growth Partner (AGP) IDs',
    icon: '👤',
    label: 'Users & AGPs',
    section: 'Manage',
  },
  activity: {
    Component: ActivityPage,
    title: 'Activity Log',
    subtitle: 'Recent admin actions',
    icon: '📜',
    label: 'Activity Log',
    section: 'System',
  },
  settings: {
    Component: SettingsPage,
    title: 'Settings',
    subtitle: 'System configuration',
    icon: '⚙️',
    label: 'Settings',
    section: 'System',
  },
};

// Explicit ordering for sidebar rendering. Same order as prototype
// (Overview → Verification → Reports → Businesses → Users → Activity → Settings).
const PAGE_ORDER = [
  'overview',
  'verification',
  'supportInbox',
  'reports',
  'businesses',
  'users',
  'activity',
  'settings',
];
const DEFAULT_PAGE = 'overview';

// Read current page slug from the URL. Unknown/missing → default.
function readPageFromUrl() {
  const p = new URLSearchParams(window.location.search).get('page');
  return PAGES[p] ? p : DEFAULT_PAGE;
}

// Push a page change to the URL without a full reload. Preserves ?admin=1.
// Overview (default) omits &page= for a cleaner URL — /?admin=1 alone lands
// on Overview.
function pushPageToUrl(slug) {
  const params = new URLSearchParams(window.location.search);
  params.set('admin', '1');
  if (slug === DEFAULT_PAGE) {
    params.delete('page');
  } else {
    params.set('page', slug);
  }
  const q = params.toString();
  const nextUrl = q ? `${window.location.pathname}?${q}` : window.location.pathname;
  window.history.pushState({}, '', nextUrl);
}

// Compute a two-letter avatar from displayName/email. Same style as V1
// avatars: first letters of the first two space-separated words, uppercase.
function computeInitials(authUser) {
  const raw = authUser?.displayName || authUser?.email || '';
  const parts = raw.split(/[\s@.]+/).filter(Boolean);
  const chars = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return chars || 'A';
}

export default function AdminShell({ authUser, onSignOut }) {
  const [currentPage, setCurrentPage] = useState(readPageFromUrl);

  // Session 5d.2 — live IDs of support chats awaiting an admin reply.
  //
  // Owned HERE, not in SupportInboxPage, because two consumers need it and
  // they must never disagree: the sidebar badge (how many) and the inbox
  // rows (which ones). SupportInboxPage's own chat list is a one-shot
  // getDocs page, so deriving "awaiting" from it would go stale the instant
  // a merchant wrote. One subscription, passed down as a prop.
  //
  // AdminShell only ever mounts after the superAdmin gate, so this never
  // runs for a non-admin.
  const [awaitingChatIds, setAwaitingChatIds] = useState([]);
  useEffect(() => {
    const unsub = subscribeToAwaitingSupportChats(setAwaitingChatIds);
    return () => {
      try { unsub?.(); } catch (_) { /* ignore */ }
    };
  }, []);
  const supportUnread = awaitingChatIds.length;

  // Browser back/forward — keep state in sync with the URL.
  useEffect(() => {
    const onPop = () => setCurrentPage(readPageFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleNav = (slug) => {
    if (slug === currentPage) return;
    pushPageToUrl(slug);
    setCurrentPage(slug);
  };

  const meta = PAGES[currentPage];
  const PageComponent = meta.Component;

  // Group items by section for sidebar rendering. Preserves PAGE_ORDER
  // within each section since we iterate PAGE_ORDER, not Object.entries.
  const sections = {};
  for (const slug of PAGE_ORDER) {
    const p = PAGES[slug];
    if (!sections[p.section]) sections[p.section] = [];
    sections[p.section].push({ slug, ...p });
  }

  const initials = computeInitials(authUser);
  const displayName = authUser?.displayName || authUser?.email || 'Admin';

  return (
    <div className="sa-app">
      <aside className="sa-sidebar">
        <div className="sa-sidebar-brand">
          <div className="sa-brand-icon">🏛️</div>
          <div>
            <div className="sa-brand-text">Aangan</div>
            <div className="sa-brand-sub">Admin</div>
          </div>
        </div>

        <div className="sa-sidebar-nav">
          {Object.entries(sections).map(([section, items]) => (
            <React.Fragment key={section}>
              <div className="sa-sidebar-section-label">{section}</div>
              {items.map((item) => (
                <div
                  key={item.slug}
                  className={`sa-sidebar-item ${currentPage === item.slug ? 'active' : ''}`}
                  onClick={() => handleNav(item.slug)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNav(item.slug);
                    }
                  }}
                >
                  <span className="sa-sidebar-item-icon">{item.icon}</span>
                  <span className="sa-sidebar-item-label">{item.label}</span>
                  {item.slug === 'supportInbox' && supportUnread > 0 && (
                    <span
                      className="sa-sidebar-badge"
                      title={`${supportUnread} chat${supportUnread === 1 ? '' : 's'} awaiting your reply`}
                    >
                      {supportUnread >= SUPPORT_UNREAD_CAP
                        ? `${SUPPORT_UNREAD_CAP}+`
                        : supportUnread}
                    </span>
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        <div className="sa-sidebar-footer">
          <div className="sa-admin-avatar">{initials}</div>
          <div className="sa-admin-info">
            <div className="sa-admin-name" title={displayName}>
              {displayName}
            </div>
            <div className="sa-admin-role">Super Admin</div>
          </div>
          <button
            className="sa-signout-btn"
            onClick={onSignOut}
            title="Sign out"
            aria-label="Sign out"
          >
            ⏻
          </button>
        </div>
      </aside>

      <main className="sa-main">
        <div className="sa-topbar">
          <div>
            <div className="sa-topbar-title">{meta.title}</div>
            <div className="sa-topbar-subtitle">{meta.subtitle}</div>
          </div>
          <div className="sa-topbar-actions">
            {/* Wired in Session 5a. Session A widened the inbox: V1 members
                can now open support chats too, so the button reads "Support
                Inbox" rather than "Aangan Connect Inbox". Clicking navigates
                to the same page as the Support Inbox sidebar item; both entry
                points lead to the same surface. Keeps prototype design intent
                (button is discoverable from every admin page) while also
                giving the sidebar item for direct navigation. */}
            <button
              className="sa-btn"
              type="button"
              onClick={() => handleNav('supportInbox')}
            >
              <span>Support Inbox</span>
            </button>
          </div>
        </div>

        <div className="sa-content">
          {/* awaitingChatIds is only meaningful to SupportInboxPage; every
              other page ignores the prop. */}
          <PageComponent authUser={authUser} awaitingChatIds={awaitingChatIds} />
        </div>
      </main>
    </div>
  );
}
